import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { PermissionsHttpService } from '@app/core/async-services/http/versioned';
import { CommonUtils } from '@app/core/utils';
import {
  BooleanLookup,
  PermissionParserService,
} from '@app/core/services/permissions/permission-parser.service';
import { UserService } from '../user';
import {
  tap,
  switchMap,
  first,
  map,
  distinctUntilChanged,
  filter,
} from 'rxjs/operators';
import { ClearStateService } from '../clear-state/clear-state.service';
import { Permissions } from '@app/core/models';
import {
  HOTEL_GROUPS_VIEW_PERMISSIONS,
  HOTEL_GROUP_SUMMARY_REPORT_PERMISSIONS,
  HOTEL_VIEW_PERMISSIONS,
} from '@app/shared/constants/permissions.constants';
import {
  MESSAGES_PERMISSION,
  TASKS_PERMISSION,
} from '@app/core/constants/permissions.constants';
import { Permission } from '@app/core/models/permissions.interface';
import { filterRequiredObject } from '@app/core/utils/rxjs.util';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private _subjectComponentPermissions =
    new BehaviorSubject<Permissions.Component | null>(null);
  private _subjectCurrentUserPermissions =
    new BehaviorSubject<Permissions.User | null>(null);
  private _permissions$: Observable<{
    component: Permissions.Component | null;
    user: Permissions.User | null;
  }>;
  private _hasMessaging$: Observable<boolean>;
  private _canAccessTasks$: Observable<boolean>;

  constructor(
    private _permissionsHttpService: PermissionsHttpService,
    private _commonUtils: CommonUtils,
    private _permissionParserService: PermissionParserService,
    private _userService: UserService,
    private _clearStateService: ClearStateService,
  ) {
    this._clearStateService.clearState$.subscribe(() => this._clearState());

    this._permissions$ = combineLatest([
      this._subjectCurrentUserPermissions,
      this._subjectComponentPermissions,
    ]).pipe(map(([user, component]) => ({ user, component })));

    this._hasMessaging$ = this._permissions$.pipe(
      filter(({ user, component }) => !!user && !!component),
      map(() => {
        return this.doesUserHavePermission(MESSAGES_PERMISSION);
      }),
      distinctUntilChanged(),
    );

    this._canAccessTasks$ = this._permissions$.pipe(
      filter(({ user, component }) => !!user && !!component),
      map(() => {
        return this.doesUserHavePermission(TASKS_PERMISSION);
      }),
      distinctUntilChanged(),
    );
  }

  get permissions$() {
    return this._permissions$;
  }

  get hasMessaging$() {
    return this._hasMessaging$;
  }

  get canAccessTasks$() {
    return this._canAccessTasks$;
  }

  get canAccessBoardBuilderV2$() {
    return this.hasPermission$(
      Permission.CanAccessBoardBuilderV2AndRoomCleanTasks,
    );
  }

  getComponentPermissions() {
    return this._subjectComponentPermissions;
  }

  getCurrentUserPermissions() {
    return this._subjectCurrentUserPermissions;
  }

  fetchComponentPermissions() {
    return this._permissionsHttpService.getComponentPermissions().pipe(
      tap((response) => this._setComponentPermissions(response.payload)),
      first(),
    );
  }

  fetchCurrentUserPermissions() {
    return this._userService.getUser().pipe(
      switchMap((user) =>
        this._permissionsHttpService.getUserPermissions(user!.WebUserId),
      ),
      tap((response) => this._setUserPermissions(response.payload)),
      first(),
    );
  }

  doesUserHavePermission(permissionExpression: string): boolean {
    if (!permissionExpression) {
      return true;
    }
    const lookup = this._createLookup();
    return this._permissionParserService.parse(permissionExpression, lookup);
  }

  get canDeleteTaskCreatedBySelf$() {
    return this.hasPermission$(
      Permission.CanCreateTasksAndAssignOrDeleteTasksCreatedByMyself,
    );
  }

  get canDeleteTaskCreatedByOthers$() {
    return this.hasPermission$(
      Permission.CanAssignAndDeleteTasksCreatedByOthers,
    );
  }

  get hasHotelGroupsViewPermission(): boolean {
    return this.doesUserHavePermission(HOTEL_GROUPS_VIEW_PERMISSIONS);
  }

  get hasHotelViewPermission(): boolean {
    return this.doesUserHavePermission(HOTEL_VIEW_PERMISSIONS);
  }

  get hasHotelGroupSummaryReportPermission(): boolean {
    return this.doesUserHavePermission(HOTEL_GROUP_SUMMARY_REPORT_PERMISSIONS);
  }

  hasPermission$(permissionExpression: string): Observable<boolean> {
    return this.permissions$.pipe(
      filter(({ user, component }) => !!user && !!component),
      map(() => {
        return this.doesUserHavePermission(permissionExpression);
      }),
      distinctUntilChanged(),
    );
  }

  getPermissionList(): Observable<Permission[]> {
    return this.permissions$.pipe(
      filterRequiredObject(),
      map(({ user, component }) => {
        const permissions: Permission[] = [];
        user.forEach((permissionId) => {
          const permissionEntry = Object.entries(component).find(([_, ids]) =>
            ids.includes(permissionId),
          );
          if (permissionEntry) {
            const [permission] = permissionEntry;
            permissions.push(permission as Permission);
          }
        });
        return permissions;
      }),
    );
  }

  private _setComponentPermissions(
    componentPermissions: Permissions.Component,
  ) {
    this._subjectComponentPermissions.next(componentPermissions);
  }

  private _setUserPermissions(userPermissions: Permissions.User) {
    this._subjectCurrentUserPermissions.next(userPermissions);
  }

  private _clearState() {
    this._subjectComponentPermissions.next(null);
    this._subjectCurrentUserPermissions.next(null);
  }

  private _createLookup(): BooleanLookup {
    const componentPermissions = this._subjectComponentPermissions.value;
    const userPermissions = this._subjectCurrentUserPermissions.value;
    const lookup = (permission: Permissions.Permission): boolean => {
      const guids =
        componentPermissions != null ? componentPermissions[permission] : [];
      return this._commonUtils.isArrayInArray(userPermissions!, guids);
    };
    return lookup as BooleanLookup;
  }
}
